import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen,
  Headphones,
  PenTool,
  Mic,
  CheckCircle2,
  ArrowRight,
  Users,
  Clock,
  Briefcase
} from 'lucide-react';
const Courses = () => {

  const [activeTab, setActiveTab] = useState('ielts');

  // --- DATA STRUCTURE ---
  const courses = [
    {
      id: 'ielts',
      name: 'IELTS',
      badge: 'Most Popular',
      subtitle: 'International English Language Testing System',
      description: 'The International English Language Testing System (IELTS) assesses the proficiency of the English language by evaluating its four skills: listening, speaking, reading, and writing. It serves as an entry-level qualification for university admissions, professional registration, and immigration purposes. Its band score ranges from 0 to 9. IELTS has two main versions: Academic training for higher education (to assess English proficiency for university admissions for undergraduate and postgraduate degrees, can also be taken up by medical doctors, nurses and pharmaceutical workers who desire to study or practice in English-speaking countries) and General Training for work and migration (to assess the English language skills of those who apply for job applications, professional registration and also for employers in English-speaking countries, to those planning to emigrate or work in an English-speaking country). IELTS for UKVI is an exclusive version required for applying for a visa to study or work or live in the UK. IELTS has two modes of taking the tests: Pen and Paper, and Computer-Based Test. Time Duration: approximately 2 hours and 45 minutes.',
      modules: [
        { title: 'Listening', icon: Headphones, desc: 'Duration: 30 minutes. Content: 4 audio recordings - 2 conversations and 2 monologues. Questions: 40 based on the recordings. Goal: To test your listening skills in English - understanding main ideas, details, opinions, and how the speech or conversation develops.' },
        { title: 'Speaking', icon: Mic, desc: 'Duration: 11–14 minutes. Format: Face-to-face interview with 3 parts. Part 1: Answer general questions about yourself. Part 2: Speak 1–2 minutes on a given topic. Part 3: Discuss the same topic in more depth. Goal: To assess your spoken English - fluency, vocabulary range, grammar accuracy, and pronunciation clarity.' },
        { title: 'Reading', icon: BookOpen, desc: 'Duration: 60 minutes. Content: 3 reading passages from books, magazines, newspapers, or journals. Questions: 40 based on the passages. Goal: To test your reading comprehension — understanding main ideas, details, inferences, and the writer\'s viewpoint.' },
        { title: 'Writing', icon: PenTool, desc: 'Duration: 60 minutes. Tasks: 2 writing tasks. Task 1: Describe visual data (graphs, charts, etc.) in at least 150 words. Task 2: Write an essay on a given topic in at least 250 words. Goal: To assess your writing ability — clarity of ideas, vocabulary range, grammatical accuracy, and relevance to the tasks.' },
      ],
      features: ['Band Score: Scored on a scale from 0 to 9', 'Results: Pen and Paper - get results in 7 days, Computer-based - get results in 3 to 5 days', 'Official Cambridge Materials']
    },
    {
      id: 'oet',
      name: 'OET',
      badge: 'Healthcare Exclusive',
      subtitle: 'Occupational English Test',
      description: 'The Occupational English Test (OET) assesses the proficiency of the English language by evaluating its four skills: listening, speaking, reading, and writing. It is specifically designed for healthcare professionals who seek job opportunities in various countries, like the United Kingdom, Australia, New Zealand, Ireland, Singapore, the Maldives and many more. Who can take up OET? Dental care, Medicine, Dietetics, Nursing, Occupational therapy, Optometry, Pharmacy, Physiotherapy, Radiography, Podiatry, Speech pathology, Veterinary sciences. OET has three modes of taking the test: OET on Paper (book at least 24 days in advance, results within 17 days), OET on Computer (book at least 7 days in advance, results within 10 days), and OET at Home (book at least 7 days before, results within 10 days).',
      modules: [
        { title: 'Listening', icon: Headphones, desc: 'The test is open to all healthcare professionals. It measures how well a candidate can communicate clearly, provide accurate details, and convey information effectively. Two certified assessors mark Part A using a set marking guide, while Parts B and C are automatically scored by computer. The entire test takes about 40 minutes and has 42 questions across three parts. Part A: 24 questions based on two five-minute extracts between a patient and a healthcare professional. Part B: Six one-minute workplace recordings with multiple-choice questions. Part C: Two longer recordings with six multiple-choice questions each.' },
        { title: 'Reading', icon: BookOpen, desc: 'The test is open to all professions and evaluates reading skills such as skimming, scanning, finding details, understanding main ideas, and interpreting meaning, tone, and opinion. It includes question types like matching, sentence completion, short answers, and multiple-choice. Qualified assessors check Part A responses using an answer key, while a computer automatically scores Parts B and C. The entire reading test lasts 60 minutes and covers general healthcare topics. Part A: 15 minutes, with four short healthcare-related texts and 20 questions. Part B: Six short workplace extracts with multiple-choice questions. Part C: Two long texts with eight multiple-choice questions about professional development.' },
        { title: 'Writing', icon: PenTool, desc: 'The writing subtest takes about 45 minutes. The first 5 minutes are for understanding the case notes, and the remaining 40 minutes are for writing. The task involves writing a letter—such as a referral, transfer, or discharge letter—based on your profession (e.g., nurse, doctor, dentist). You may also need to provide advice, share information with a patient or carer, or respond to a complaint. Six assessment criteria are used to evaluate your writing, and qualified assessors review your work accordingly.' },
        { title: 'Speaking', icon: Mic, desc: 'The speaking subtest lasts about 20 minutes and varies by profession. It uses role-plays based on real workplace situations. Candidates take on the role of a healthcare professional (such as a doctor, nurse, or pharmacist), while the other participant acts as a patient, client, carer, or family member. The test begins with a short warm-up, followed by 3 minutes of preparation for each role-play. Each simulation lasts up to 5 minutes. The test evaluates performance using four languages and five clinical communication criteria. All role-plays are recorded, and trained assessors mark them independently.' },
      ],
      features: ['Score Chart: OET Numeric Score 500-450 = Grade A (IELTS 8.0-9.0)', 'OET 440-400 = Grade B+ (IELTS 7.5)', 'OET 390-350 = Grade B (IELTS 7.0)', 'OET 340-300 = Grade C+ (IELTS 6.5)', 'OET 290-200 = Grade C (IELTS 5.5-6.0)']
    },
    {
      id: 'pte',
      name: 'PTE',
      badge: 'Fast Results',
      subtitle: 'Pearson Test of English',
      description: 'The Pearson Test of English (PTE) is a secure English language testing system used for academic purposes and visa applications worldwide. It is accepted by over 3,500 universities and colleges in countries such as the UK, Australia, Canada, New Zealand, and many others. The PTE enables individuals to quickly demonstrate their English proficiency, which supports their educational, work, and migration opportunities. By proving their language skills through PTE, people can open doors to global study and career advancement. Format of the Test: The Pearson Test of English (PTE) is the first fully computer-based English exam designed to assess international English, not limited to British or American varieties. It evaluates academic English skills and is taken at authorized test centres or test-friendly locations. The test lasts for about two hours, and results are released within 72 hours of completion. By combining AI scoring with human expertise, PTE ensures fairness and minimizes bias. Versions of PTE: PTE Academic / UKVI (20 different question types, score ranges from 10 to 90), PTE Core (19 different question types, score ranges from 10 to 90), PTE Home (computer-based English language exam designed for UK visa and immigration purposes, evaluates basic speaking and listening skills according to CEFR levels A1, A2, and B1, you receive a pass or fail result).',
      modules: [
        { title: 'Speaking & Writing', icon: Mic, desc: 'This combined section forms the first part of the Pearson Test of English (PTE) and evaluates both speaking and writing skills. Task examples include summarizing written text, writing an essay, and retelling a lecture.' },
        { title: 'Reading', icon: BookOpen, desc: 'The Reading section of the Pearson Test of English (PTE) lasts for 30 minutes and evaluates your ability to understand written texts. It includes tasks such as multiple-choice questions (single and multiple answers), filling in blanks, and reordering sentences.' },
        { title: 'Listening', icon: Headphones, desc: 'The Listening section of the Pearson Test of English (PTE) lasts for 40 minutes and includes various tasks such as summarizing spoken texts, answering multiple-choice questions, listening to dictation, correcting sentences, and identifying missing words.' },
        { title: 'Test Format', icon: PenTool, desc: 'For academic purposes, the Pearson Test of English (PTE) consists of three sections: speaking and writing (combined as one test), reading, and listening, each scored separately. It is a fully computer-based exam that can be taken either at an authorized test center or from home.' },
      ],
      features: ['Results within 72 hours', 'AI scoring with human expertise', 'Accepted by 3,500+ universities', 'Score ranges from 10 to 90']
    },
    {
      id: 'det',
      name: 'Duolingo',
      badge: 'Study Abroad',
      subtitle: 'Duolingo English Test',
      description: 'The Duolingo English Test is an online adaptive English proficiency test to assess listening, speaking, reading and writing skills in English. Though not as universal as IELTS, it serves as an alternative to traditional tests like IELTS and TOEFL, as it is more convenient and cost-effective. Its score (10 to 160) is accepted by over 5,500 universities and employers for admissions and hiring. Why can one opt for DET? Online and convenient – no test centres and can be taken even from home with just a computer, webcam and internet connection. Adaptive format – the difficulty of the questions gets adjusted based on the performance throughout the test. Skills assessed throughout the test – Listening, Speaking, Reading and Writing. Duration – approximately 1 hour. Score – ranges from 10 to 160. Result – within 48 hours.',
      modules: [
        { title: 'Introduction', icon: Users, desc: '5 minutes: Introduced to the test format and rules' },
        { title: 'Adaptive Test Section', icon: BookOpen, desc: '45 minutes: A series of interactive and adaptive questions to assess the skills. Questions do not follow any particular order and they are not grouped by type.' },
        { title: 'Speaking & Writing Sample', icon: Mic, desc: '10 minutes: Open-ended questions will be given for a writing sample and a video recording, which will be sent to the institutions along with your score.' },
        { title: 'Test Structure', icon: PenTool, desc: 'Complete test includes Introduction (5 min), Adaptive test section (45 min), and Speaking and Writing Sample (10 min).' },
      ],
      features: ['Online and convenient - take from home', 'Adaptive format - questions adjust to your level', 'Results within 48 hours', 'Accepted by 5,500+ universities']
    },
    {
      id: 'spoken-english',
      name: 'Spoken English',
      badge: 'Confidence Booster',
      subtitle: 'Practical English Communication Program',
      description: 'The Spoken English program is designed to help learners speak English confidently and fluently in everyday situations. This course focuses on practical communication rather than grammar alone, enabling learners to use English naturally in real-life and professional environments. The program covers vocabulary development, pronunciation improvement, sentence formation, and conversational skills. Learners are trained to communicate effectively in public speaking, interviews, group discussions, and workplace interactions. By emphasizing listening skills, role plays, and response techniques, the course helps individuals overcome the fear of speaking English and build strong communication confidence.',
      modules: [
        {
          title: 'Vocabulary & Sentence Formation',
          icon: BookOpen,
          desc: 'This module focuses on building basic to advanced vocabulary and forming correct sentences used in everyday conversations and professional settings.'
        },
        {
          title: 'Pronunciation & Accent',
          icon: Mic,
          desc: 'Learners are trained in correct pronunciation, accent neutralization, and clarity of speech to sound natural and confident while speaking English.'
        },
        {
          title: 'Speaking & Communication Skills',
          icon: Users,
          desc: 'This section develops public speaking skills, one-to-one conversations, group discussions, role plays, and commonly used phrases for daily life and workplace communication.'
        },
        {
          title: 'Listening & Interview Skills',
          icon: Headphones,
          desc: 'Focuses on listening skills, response techniques, self-introduction, and interview preparation to help learners communicate effectively and professionally.'
        }
      ],
      features: [
        'Basic to advanced English training',
        'Confidence-building speaking practice',
        'Real-life conversation focus',
        'Interview and workplace communication skills'
      ]
    }
    ,
    {
      id: 'soft-skills',
      name: 'Soft Skills',
      badge: 'Career Ready',
      subtitle: 'Professional & Personal Development Program',
      description: 'The Soft Skills Training program is designed to develop essential personal and professional skills required for career success and workplace effectiveness. This program focuses on enhancing communication abilities, personality development, emotional intelligence, and professional behavior. Participants are trained to manage time effectively, work collaboratively in teams, and demonstrate leadership and decision-making skills. Through practical activities such as public speaking, presentations, interview preparation, and resume presentation, learners build confidence, improve their attitude, and become workplace-ready professionals.',
      modules: [
        {
          title: 'Communication & Personality Development',
          icon: Mic,
          desc: 'Focuses on verbal and non-verbal communication skills, personality development, self-confidence, and professional interaction in the workplace.'
        },
        {
          title: 'Leadership & Teamwork',
          icon: Users,
          desc: 'Develops teamwork, collaboration, leadership qualities, decision-making abilities, and effective interpersonal relationships.'
        },
        {
          title: 'Time Management & Problem Solving',
          icon: Clock,
          desc: 'Covers time management, goal setting, problem-solving techniques, and critical thinking skills for professional growth.'
        },
        {
          title: 'Professional Readiness',
          icon: Briefcase,
          desc: 'Includes workplace etiquette, emotional intelligence, stress management, interview skills, resume presentation, public speaking, and presentation skills.'
        }
      ],
      features: [
        'Career-focused soft skills training',
        'Interview and presentation preparation',
        'Leadership and teamwork development',
        'Workplace etiquette and professional behavior'
      ]
    },
    {
  id: 'spoken-english-kids',
  name: 'Spoken English for Kids',
  badge: 'Fun Learning',
  subtitle: 'Interactive English Program for Children',
  description: 'The Spoken English for Kids program is specially designed to help children speak English confidently in a fun, friendly, and supportive environment. The course focuses on developing speaking, listening, and vocabulary skills through interactive and activity-based learning. Children are encouraged to communicate naturally through games, storytelling, songs, and role plays. By building strong foundations in pronunciation, phonics, and everyday conversations, this program helps children gain confidence and communicate comfortably in school and daily life.',
  modules: [
    {
      title: 'Basic English & Vocabulary',
      icon: BookOpen,
      desc: 'Introduces basic English words, simple sentences, and vocabulary building through fun games, pictures, and interactive activities.'
    },
    {
      title: 'Phonics & Pronunciation',
      icon: Mic,
      desc: 'Focuses on phonics, correct pronunciation, and clear speech to help children develop strong speaking foundations.'
    },
    {
      title: 'Speaking & Storytelling',
      icon: Users,
      desc: 'Encourages everyday conversations, storytelling, picture descriptions, and stage speaking to build confidence and fluency.'
    },
    {
      title: 'Listening & Fun Activities',
      icon: Headphones,
      desc: 'Improves listening skills and response practice through songs, role plays, stories, and child-friendly activities.'
    }
  ],
  features: [
    'Fun and interactive learning environment',
    'Activity-based teaching methods',
    'Confidence building for kids',
    'Improves fluency for school and daily life'
  ]
}

  ];

  const activeCourse = courses.find(c => c.id === activeTab);

  return (
    <div className="bg-slate-50 min-h-screen font-sans">
      

      {/* =========================================
          HERO SECTION
      ========================================= */}
      <section className="relative pt-32 pb-12 md:pb-20 bg-slate-900 text-white overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-red-600/10 rounded-full blur-[80px] pointer-events-none -translate-y-1/2 translate-x-1/2"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-red-500 font-bold tracking-widest uppercase text-xs mb-4 block">
              World-Class Curriculum
            </span>
            <h1 className="text-3xl text-gray-100 md:text-6xl font-serif font-bold mb-4 md:mb-6 leading-tight">
              Master the Language. <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">
                Unlock the World.
              </span>
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8 text-sm md:text-xl">
              Specialized training for IELTS, OET, and PTE designed for speed and precision.
            </p>
          </motion.div>
        </div>
      </section>



      {/* =========================================
          COURSE COCKPIT
      ========================================= */}
      <section className="py-8 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">

            {/* 1. NAVIGATION (Sticky + Snap Scroll) */}
            <div className="w-full lg:w-1/4  top-[70px] z-30 bg-slate-50/95 backdrop-blur-sm pt-2 pb-2 lg:static lg:bg-transparent lg:p-0">

              {/* MOBILE TIP: Swipe Indicator */}
              <div className="lg:hidden flex items-center justify-between px-1 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Select Course</span>
                <span className="text-[10px] font-bold text-red-500 flex items-center gap-1 animate-pulse">
                  Swipe <ArrowRight size={10} />
                </span>
              </div>

              <div className="relative group">
                {/* FADE MASK: Visual cue for horizontal scrolling */}
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-slate-50 to-transparent z-10 pointer-events-none lg:hidden"></div>

                {/* SCROLL CONTAINER */}
                <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-visible pb-4 lg:pb-0 no-scrollbar items-center lg:items-stretch px-1 snap-x snap-mandatory">
                  {courses.map((course) => (
                    <button
                      key={course.id}
                      onClick={() => setActiveTab(course.id)}
                      className={`relative p-4 text-left rounded-xl border transition-all duration-300 flex-shrink-0 w-[85%] sm:w-64 lg:w-full snap-center ${activeTab === course.id
                        ? 'bg-slate-900 border-slate-900 shadow-xl scale-[1.02] lg:scale-105 z-10'
                        : 'bg-white border-slate-200 hover:border-red-200'
                        }`}
                    >
                      <div className="flex justify-between items-center mb-1 lg:mb-2">
                        <span className={`text-xl lg:text-2xl font-bold font-serif ${activeTab === course.id ? 'text-white' : 'text-slate-900'}`}>
                          {course.name}
                        </span>
                        {activeTab === course.id && (
                          <motion.div layoutId="active-dot" className="w-2 h-2 rounded-full bg-red-500" />
                        )}
                      </div>
                      <span className={`text-[10px] lg:text-xs font-medium uppercase tracking-wider block ${activeTab === course.id ? 'text-slate-400' : 'text-slate-500'}`}>
                        {course.subtitle}
                      </span>
                    </button>
                  ))}

                  {/* Spacer for mobile scroll */}
                  <div className="w-4 flex-shrink-0 lg:hidden"></div>
                </div>
              </div>
            </div>

            {/* 2. CONTENT DISPLAY (Right Side) */}
            <div className="w-full lg:w-3/4 min-h-[500px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white rounded-2xl md:rounded-3xl border border-slate-200 p-6 md:p-10 shadow-xl shadow-slate-200/50"
                >
                  {/* Badge & Title */}
                  <div className="mb-6 md:mb-8">
                    <div className="inline-block bg-red-50 text-red-700 px-3 py-1 rounded-full text-[10px] md:text-xs font-bold uppercase tracking-wide mb-3 border border-red-100">
                      {activeCourse.badge}
                    </div>
                    <h2 className="text-2xl md:text-4xl font-serif font-bold text-slate-900 leading-tight">
                      {activeCourse.subtitle}
                    </h2>
                  </div>

                  {/* Description */}
                  <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-8 md:mb-10 border-l-4 border-red-500 pl-4 md:pl-6 text-justify">
                    {activeCourse.description}
                  </p>

                  {/* Modules Grid */}
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 md:mb-6">Course Modules</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-8 md:mb-10">
                    {activeCourse.modules.map((mod, i) => (
                      <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100 hover:bg-white hover:shadow-md transition-all">
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-lg flex items-center justify-center text-red-600 border border-slate-200 shrink-0 shadow-sm">
                          <mod.icon size={20} className="md:w-6 md:h-6" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 text-sm md:text-base">{mod.title}</h4>
                          <p className="text-xs text-justify md:text-sm text-slate-500 leading-snug mt-1">{mod.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Features & CTA Card */}
                  <div className="bg-slate-900 rounded-xl md:rounded-2xl p-6 md:p-8 text-white flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div className="flex-1 w-full">
                      <h4 className="font-serif font-bold text-base text-gray-100 md:text-lg mb-4">Why join this batch?</h4>
                      <ul className="space-y-3 md:space-y-2">
                        {activeCourse.features.map((feat, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                            <CheckCircle2 size={16} className="text-red-500 shrink-0" /> {feat}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex flex-col gap-3 w-full md:w-auto">
                      <Link
                        to="/book-free-demo-class"
                        className="w-full px-6 py-3 md:px-8 md:py-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl shadow-lg shadow-red-600/20 text-center transition-all active:scale-95"
                      >
                        Book Free Demo
                      </Link>
                      <Link to="/contact" className="text-center text-xs md:text-sm text-slate-400 hover:text-white py-2">
                        View Full Syllabus
                      </Link>
                    </div>
                  </div>

                </motion.div>
              </AnimatePresence>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          COMPARISON TABLE
      ========================================= */}
      <section className="py-12 md:py-20 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-slate-900 mb-8 text-center">Compare Formats</h2>

          <div className="overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0">
            <table className="w-full text-left border-collapse min-w-[500px]">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="py-4 font-bold text-slate-900 w-1/4">Feature</th>
                  <th className="py-4 font-bold text-slate-500 w-1/4">IELTS</th>
                  <th className="py-4 font-bold text-slate-500 w-1/4">PTE</th>
                  <th className="py-4 font-bold text-slate-500 w-1/4">OET</th>
                </tr>
              </thead>
              <tbody className="text-slate-600 text-sm">
                <tr className="border-b border-slate-100">
                  <td className="py-4 font-bold text-slate-900">Best For</td>
                  <td className="py-4">Canada / UK</td>
                  <td className="py-4">Australia</td>
                  <td className="py-4">Doctors/Nurses</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 font-bold text-slate-900">Format</td>
                  <td className="py-4">Paper/Computer</td>
                  <td className="py-4">Computer (AI)</td>
                  <td className="py-4">Paper/Computer</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 font-bold text-slate-900">Duration</td>
                  <td className="py-4">2h 45m</td>
                  <td className="py-4">2 hours</td>
                  <td className="py-4">2h 45m</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Courses;